import React from 'react';
import { FaPlay } from 'react-icons/fa';
import PropTypes from 'prop-types';
import {
    SlideContainer,
    SlideContainerWithHeader,
    SlideList,
} from '../../SlideComponents';
import { REGION, COUNTRY } from '../../../../../../commons/CountryUtils';

const headingCss = 'text-xl md:text-3xl uppercase font-bold pb-4';
const bodyCss =
    'text-base md:text-lg text-gray-900 text-justify float-left pt-4';
const disclaimerCss = 'text-sm text-white mt-2 mb-4 opacity-50';
const textBoxCss = 'pb-1 md:pb-4';
const footerTextCss = 'pb-1 md:pb-4 text-xs italic text-gray-700';

const Slides = {
    audioPath: `${process.env.PUBLIC_URL}/audio/${REGION.AMERICA}/${COUNTRY.CANADA}`,
    totalSlides: 20,
    component: ({ currentSlide, nextHandler }) => {
        switch (currentSlide) {
            case 1:
                return (
                    <SlideContainer
                        style={{ backgroundColor: 'black', color: '#8BA7C3' }}
                    >
                        <div>
                            <h1 className={headingCss}>
                                D. E. Shaw Consultant Compliance Program
                                Instructions
                            </h1>
                            <div className=" text-base md:text-xl">
                                This is an audio presentation. After viewing the
                                presentation, please confirm that you have read
                                and understood the obligations by clicking on
                                the checkbox at the bottom.
                            </div>
                            <div className="py-12 text-center">
                                <span
                                    className="text-white text-6xl inline-block px-12 py-6 bg-green-600 rounded mb-4 cursor-pointer"
                                    style={{ borderRadius: '0.75rem' }}
                                    onClick={nextHandler}
                                    data-testid="start-compliance-presentation"
                                >
                                    <FaPlay />
                                </span>
                                <div className="text-white text-lg text-center">
                                    Please click on the button above to start
                                    the presentation.
                                </div>
                            </div>
                            <div className={disclaimerCss}>
                                <span>
                                    Each slide has audio which may take some
                                    time&nbsp;(~ 1 min) to download.
                                </span>
                                <br />
                                <span>
                                    If you have partially completed the
                                    presentation, you may skip the viewed slides
                                    by clicking <b>NEXT SLIDE</b> button at the
                                    bottom of each slide.
                                </span>
                            </div>
                        </div>
                    </SlideContainer>
                );
            case 2:
                return (
                    <SlideContainer
                        style={{ backgroundColor: 'black', color: '#8BA7C3' }}
                    >
                        <h1 className="text-xl md:text-3xl pb-6 uppercase">
                            D. E. Shaw Industry Consultant Program:{' '}
                        </h1>
                        <h2 className={headingCss}>
                            Training for New Consultants
                        </h2>
                    </SlideContainer>
                );
            case 3:
                return (
                    <SlideContainerWithHeader header="Overview">
                        <main className={bodyCss}>
                            <SlideList
                                list={[
                                    'Description of the Industry Consultant Program (ICP)',
                                    'Consultant Obligations',
                                    'Identifying and Avoiding Impermissible Disclosures',
                                    'Participating in the ICP',
                                    'Conclusion',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 4:
                return (
                    <SlideContainerWithHeader header="Description of ICP">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                The Industry Consultant Program
                                (&quot;ICP&quot;) is intended to assist the D.
                                E. Shaw group in developing specialized
                                knowledge and expertise to support its
                                investment strategies. Common topics on which
                                the D. E. Shaw group may seek the expertise of a
                                consultant include:
                            </div>
                            <SlideList
                                list={[
                                    'Industry Trends',
                                    'Competitive Landscape',
                                    'Management Style',
                                    'Product Comparison and Understanding',
                                    'Mergers and Acquisitions',
                                ]}
                            />
                            <div>
                                Like other consultants, you have been invited to
                                participate in the ICP based on your expertise
                                and experience in a subject area that is of
                                interest to the D.&nbsp;E.&nbsp;Shaw&nbsp;group.
                            </div>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 5:
                return (
                    <SlideContainerWithHeader header="Consultant Obligations">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                What will be required of you?
                            </div>
                            <SlideList
                                list={[
                                    'Provide us with accurate and up-to-date information.',
                                    'Ensure that you only participate in consultations if it is appropriate to do so.',
                                    'Abide by applicable law and regulation, as well as any obligations you owe to us.',
                                    "Contact the ICP Consultation Coordinator or the D. E. Shaw group's Compliance department with any concerns or questions that come up during this presentation and/or during your participation in the ICP itself.",
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 6:
                return (
                    <SlideContainerWithHeader header="Consultant Obligations (cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Obligation 1: Provide us with accurate and
                                up-to-date information.
                            </div>
                            <SlideList
                                list={[
                                    'You must complete the ICP Consultant Application Form as comprehensively as possible.',
                                    'If you have a scheduled consultation, update us as promptly as possible before the consultation if any information that you previously provided to us has changed.',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 7:
                return (
                    <SlideContainerWithHeader header="Consultant Obligations (cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Obligation 2: Ensure that you only participate
                                in appropriate consultations
                            </div>
                            <ul className="list-disc pl-6 pb-4">
                                <li className="pl-6 leading-relaxed">
                                    {' '}
                                    You may not participate in the ICP if you:{' '}
                                </li>
                                <div className="pl-6">
                                    <SlideList
                                        style={{ listStyleType: 'circle' }}
                                        list={[
                                            'work for or on behalf of the subject company or have done so in the last six months (although this period may be extended in some circumstances)',
                                            'are a professional securities analyst.',
                                        ]}
                                    />
                                </div>
                            </ul>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 8:
                return (
                    <SlideContainerWithHeader header="Consultant Obligations (cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Obligation 2: Ensure that you only participate
                                in appropriate consultations (cont.)
                            </div>
                            <SlideList
                                list={[
                                    "You must ensure that you are permitted by your employer to engage in paid consultations and that your participation in the ICP would not violate your employer's rules or other obligations that you owe to your employer or to third parties. (This does not apply to those who are self-employed.)",
                                    'The subject of the consultation should be something you and your employer (if relevant) would be prepared to discuss with an outside party (such as the D. E. Shaw group).  You should ensure that you are comfortable that you may share your knowledge without breaching any confidences or agreements to which you and your employer (if relevant) are subject.',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 9:
                return (
                    <SlideContainerWithHeader header="Consultant Obligations (cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Obligation 3: Abide by applicable law and
                                regulation, as well as any obligations you owe
                                to us.
                            </div>
                            <ul className="list-disc pl-6 pb-4">
                                <li className="pl-6 leading-relaxed">
                                    {' '}
                                    Your obligations to the
                                    D.&nbsp;E.&nbsp;Shaw&nbsp;group:
                                </li>
                                <div className="pl-6">
                                    <SlideList
                                        style={{
                                            listStyleType: 'circle',
                                            paddingBottom: '0px',
                                        }}
                                        list={[
                                            'You must keep the subject matter of all consultations confidential, whether or not you participate in the consultation.',
                                            'If you participate in a consultation, you must keep both the contents of the consultation and the fact that it happened confidential. Do not refer to your consultation work on your resume or LinkedIn profile.',
                                            'You must comply with all provisions of the Terms of Participation.',
                                        ]}
                                    />
                                </div>
                                <li className="pl-6 leading-relaxed">
                                    Identify and avoid impermissible disclosures
                                    in order to comply with applicable laws and
                                    regulations.
                                </li>
                            </ul>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 10:
                return (
                    <SlideContainerWithHeader header="Consultant Obligations (cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Obligation 4: Contact the ICP Consultation
                                Coordinator or the
                                D.&nbsp;E.&nbsp;Shaw&nbsp;group Compliance
                                department with any concerns or questions that
                                come up during this presentation.
                            </div>
                            <div>
                                You can contact the
                                D.&nbsp;E.&nbsp;Shaw&nbsp;group Compliance
                                department by calling 212-478-0000 and asking
                                for the Compliance department.
                            </div>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 11:
                return (
                    <SlideContainerWithHeader header="Identifying and Avoiding Impermissible Disclosures">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Identifying and Avoiding Impermissible
                                Disclosures:
                            </div>
                            <br />
                            <SlideList
                                list={[
                                    'Confidential Third-Party Material;',
                                    'Material Non-Public Information (MNPI); and',
                                    'Intellectual Property',
                                ]}
                            />
                            <p>
                                These concepts are discussed on subsequent
                                slides
                            </p>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 12:
                return (
                    <SlideContainerWithHeader header="Identifying and Avoiding Impermissible Disclosures (Cont.)">
                        <main className={bodyCss}>
                            <div className="block pb-4">
                                <span className="font-bold pr-2">A. </span>
                                Confidential Third-Party Material
                            </div>
                            <SlideList
                                list={[
                                    "Information resulting from your or another's close or fiduciary relationship to the subject of the consultation.",
                                    // eslint-disable-next-line react/jsx-key
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: 'Information that was obtained improperly (<i>e.g.</i>, by means of lavish gifts or entertainment, through computer hacking, or by accessing confidential government documents).',
                                        }}
                                    />,
                                    'Information that you are contractually forbidden to pass along',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 13:
                return (
                    <SlideContainerWithHeader header="Identifying and Avoiding Impermissible Disclosures (Cont.)">
                        <main className={bodyCss}>
                            <div className={textBoxCss}>
                                <span className="font-bold pr-2">B. </span>{' '}
                                Material Non-Public Information (MNPI)*
                            </div>
                            <div className="text-base">
                                <div className={textBoxCss}>
                                    &ldquo;Material information&rdquo; is any
                                    information (including both material facts
                                    and material changes) relating to the
                                    business and affairs of a company that
                                    significantly affects or would reasonably be
                                    expected to have a significant effect on the
                                    market price or value of a company&rsquo;s
                                    securities.
                                </div>
                                <div className={textBoxCss}>
                                    Examples of potentially &ldquo;material
                                    information&rdquo;
                                </div>
                                <SlideList
                                    list={[
                                        'Earnings information (including quarterly revenues and margins)',
                                        'Mergers, acquisitions, joint ventures, and tender offers',
                                        'New product development and resource discoveries',
                                        'Significant developments regarding customers and suppliers',
                                        'Significant credit arrangements',
                                        'Changes in ownership, control, board or management',
                                        'Disagreements between an issuer and its auditor regarding significant accounting issues',
                                        'Bankruptcies or receiverships',
                                    ]}
                                />
                            </div>
                            <div className={footerTextCss}>
                                *This presentation only provides a summary of
                                certain Canadian securities laws which should
                                not be taken as comprehensive and securities
                                laws of other jurisdictions (such as U.S.
                                securities laws) may apply and need to be
                                complied with
                            </div>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 14:
                return (
                    <SlideContainerWithHeader header="Identifying and Avoiding Impermissible Disclosures (Cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                <span className="font-bold pr-2">B. </span>{' '}
                                Material Non-Public Information (MNPI) (cont.):
                            </div>
                            <div className="pb-4">
                                Information is &ldquo;non-public&ldquo; until it
                                has been &ldquo;generally disclosed&ldquo;,
                                which typically means that the information has
                                been effectively disseminated to the marketplace
                                (for example through a regulatory filing or
                                official press release by the subject company
                                through a widely circulated news or wire
                                service) and public investors have been given a
                                reasonable time to absorb the information.
                            </div>
                            <div className="pb-4">
                                Examples of &ldquo;non-public&rdquo; information
                            </div>
                            <SlideList
                                list={[
                                    'Information that would routinely be disclosed by the company in securities filings but has not yet been filed (e.g., annual or interim financial information or earnings guidance)',
                                    'Information discussed only within the company and which is protected by company confidentiality policies',
                                    'The status or content of negotiations with a key customer or supplier',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 15:
                return (
                    <SlideContainerWithHeader header="Identifying and Avoiding Impermissible Disclosures (Cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                <span className="font-bold pr-2">B. </span>{' '}
                                Material Non-Public Information (MNPI) (cont.):
                            </div>
                            <ul className="list-disc pl-6 pb-4">
                                <li className="pl-6 leading-relaxed">
                                    {' '}
                                    MNPI concerns apply with respect to all
                                    securities, including equity and debt
                                    securities.
                                </li>
                                <li className="pl-6 leading-relaxed">
                                    {' '}
                                    The Terms of Participation are designed to
                                    minimize the risk that MNPI will be
                                    disclosed to employees of the D. E. Shaw
                                    group{' '}
                                </li>
                                <li className="pl-6 leading-relaxed">
                                    {' '}
                                    What is the concern with MNPI?
                                </li>
                                <div className="pl-6">
                                    <SlideList
                                        style={{ listStyleType: 'circle' }}
                                        list={[
                                            <div key="para-1">
                                                In general, trading while in
                                                possession of MNPI is a
                                                violation of securities laws and
                                                a breach of the criminal code,
                                                and communicating MNPI to
                                                anyone, if you believe or have
                                                reason to believe that person
                                                may trade on or misuse such
                                                information (“tipping”), is also
                                                illegal
                                            </div>,
                                        ]}
                                    />
                                </div>
                                <li className="pl-6 leading-relaxed">
                                    {' '}
                                    If you are in possession of MNPI about a
                                    company you may not communicate with an
                                    employee of the D. E. Shaw group about that
                                    company, even if you believe you are not
                                    revealing any MNPI.
                                </li>
                            </ul>
                        </main>
                    </SlideContainerWithHeader>
                );
            case 16:
                return (
                    <SlideContainerWithHeader header="Identifying and Avoiding Impermissible Disclosures (Cont.)">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                <span className="font-bold pr-2">C. </span>{' '}
                                Intellectual Property
                            </div>
                            <div className="pb-4">
                                Intellectual property includes works of
                                authorship; ideas, inventions, or processes;
                                trade secrets; or proprietary information that
                                is confidential or subject to a contractual
                                obligation of nondisclosure or unauthorized use,
                                regardless of whether the information is your
                                property or the property of some other
                                individual or organization.
                            </div>
                            <div className="pb-4">
                                Examples of potential Intellectual Property:
                            </div>
                            <SlideList
                                list={[
                                    'Customer lists',
                                    'Business plans',
                                    'New product ideas',
                                    'Draft annual reports',
                                    'Retail shelf space negotiations',
                                    'Purchaser demand forecasts',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 17:
                return (
                    <SlideContainerWithHeader header="Participating in the ICP">
                        <main className={bodyCss}>
                            <div className="pb-4">
                                Preparing for a consultation
                            </div>
                            <SlideList
                                list={[
                                    'Think through the subject of the consultation and refresh your recollection on important and current issues.',
                                    'Consider whether there are any necessary boundaries to the consultation and be prepared to explain those boundaries to employees of the D. E. Shaw group at the beginning of the consultation',
                                    'Bring any pre-consultation concerns to the attention of the ICP Consultation Coordinator.',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 18:
                return (
                    <SlideContainerWithHeader header="Participating in the ICP">
                        <main className={bodyCss}>
                            <div className="pb-4">During the Consultation</div>
                            <SlideList
                                list={[
                                    'Explain your ideas and opinions clearly; provide the basis and/or sources of your ideas and opinions.',
                                    'Do not speculate, guess or exaggerate your knowledge.',
                                    'Alert the questioner if answering the questions would reveal confidential information or other information that should not be provided; do not answer any such questions.',
                                    'Keep the Terms of Participation and this training presentation in mind.',
                                    'Alert the questioner if you have mistakenly provided confidential or other inappropriate information.',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 19:
                return (
                    <SlideContainerWithHeader header="Participating in the ICP">
                        <main className={bodyCss}>
                            <div className="pb-4">After the consultation</div>
                            <SlideList
                                list={[
                                    'Contact the D. E. Shaw group Compliance department immediately by calling 212-478-0000 and asking for Compliance if you have inadvertently disclosed impermissible material or you have other concerns about your consultation.',
                                    'Do not contact the questioner directly after the consultation; instead, route all correspondence through the ICP Consultation Coordinator.',
                                ]}
                            />
                        </main>
                    </SlideContainerWithHeader>
                );
            case 20:
                return (
                    <SlideContainerWithHeader header="Conclusion">
                        <main className={bodyCss}>
                            <div>
                                <br />
                                Thank you for your participation in the D. E.
                                Shaw group&#39;s ICP and your commitment to
                                abide by the Terms of Participation.
                            </div>
                        </main>
                    </SlideContainerWithHeader>
                );
            default:
                return <SlideContainer></SlideContainer>;
        }
    },
};

Slides.propTypes = {
    currentSlide: PropTypes.number,
    nextHandler: PropTypes.func,
};
export default Slides;
